import { useState } from "react";
import { FieldArray } from "formik";
import { Add } from "@mui/icons-material";
import { IconCheck } from "@tabler/icons";
import { InputField } from "../../../../utils/FormFields";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { FormikProps, FormikValues, useFormikContext } from "formik";
import { Button, Grid, IconButton, Typography, Box, Tooltip } from "@mui/material";

const formatDate = (isoDateString: Date, timezoneOffset: number) => {
  const adjustedDate = new Date(isoDateString);
  adjustedDate.setMinutes(adjustedDate.getMinutes() + timezoneOffset);

  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  } as Intl.DateTimeFormatOptions;

  return new Intl.DateTimeFormat("en-US", options).format(adjustedDate);
};

const CriticNotes = () => {
  const timezoneOffset = new Date().getTimezoneOffset(); 
  const formik: FormikProps<FormikValues> = useFormikContext();

  const [noteNumber, setNoteNumber] = useState<number>(0);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const handleSaveNote = (note: any, noteNumber: number) => {
    const currentTime = new Date();
    const timeWithOffset = new Date(currentTime.getTime() - timezoneOffset * 60 * 1000);
      
    const updatedCriticNotes = formik?.values?.Patient?.CriticNotes.map((item: any) => {
      if (item.NoteNumber === noteNumber) {        
        return {
          ...item,
          DateModified: timeWithOffset.toISOString(),
        };
      }
      return item;
    });
  
    formik.setFieldValue('Patient.CriticNotes', updatedCriticNotes);
  };
  

  return (
    <>
      <FieldArray
        name="Patient.CriticNotes"
        render={(arrayHelpers) => (
          <>
            <Grid item xs={12} sx={{ textAlign: "right" }}>
              <Tooltip title="Add Critic Note">
                <Button
                  onClick={() => {
                    const currentTime = new Date();
                    const timezoneOffset = currentTime.getTimezoneOffset();
                    const timeWithOffset = new Date(currentTime.getTime() - timezoneOffset * 60 * 1000);

                    const lastNote = formik?.values?.Patient.CriticNotes.reduce((prevNote: any, currentNote: any) => {
                      return currentNote.NoteNumber > prevNote.NoteNumber ? currentNote : prevNote;
                    }, { NoteNumber: -1 });
                    
                    const lastNoteNumber = lastNote.NoteNumber + 1;

                    setNoteNumber((prevNoteNumber) => prevNoteNumber + 1);
                    arrayHelpers.push({
                      DateCreated: timeWithOffset,
                      DateModified: timeWithOffset,
                      Note: "",
                      NoteNumber: lastNoteNumber,
                    });
                  }}
                  color="primary"
                  variant="outlined"
                  sx={{ p: 1, mb: 4 }}
                >
                  <Add />
                </Button>
              </Tooltip>  
            </Grid>
            <Grid container spacing={3}>
              {formik?.values?.Patient?.CriticNotes.map((note: string, index: number) => (
                <>
                  <Grid item xs={10} sm={5} md={4} key={"CriticNote" + index}>
                    <Grid sx={{ display: "flex", alignItems: "center" }}>
                      <Box sx={{ pl: 1}}>
                        <Typography color="Secondary" sx={{ pb: 1 }}>
                          Created:{" "}
                          {formik?.values?.Patient?.CriticNotes[index]?.DateCreated
                            ? formatDate(
                                formik?.values?.Patient?.CriticNotes[index]?.DateCreated,
                                timezoneOffset
                              )
                            : ""}
                        </Typography>
                        <Typography color="Secondary" sx={{ pb: 1 }}>
                          Modified:{" "}
                          {formik?.values?.Patient?.CriticNotes[index]?.DateModified
                            ? formatDate(
                                formik?.values?.Patient?.CriticNotes[index]?.DateModified,
                                timezoneOffset
                              )
                            : ""}
                        </Typography>
                      </Box>
                      <Grid item xs={4} sm={1} sx={{ textAlign: "right", marginLeft: "auto" }}>
                        <IconButton onClick={() => arrayHelpers.remove(index)}>
                          <Tooltip title="Delete Note">
                            <DeleteOutlineIcon color="error" />
                          </Tooltip>
                        </IconButton>
                      </Grid>
                    </Grid>                    
                    <InputField
                      name={`Patient.CriticNotes[${index}].Note`}
                      value={note}
                      label="Note"
                      multiline
                      rows={6}
                      fullWidth
                      variant="outlined"                      
                      onFocus={() => {
                        setFocusedIndex(index);
                      }}                                                    
                    />     
                    {focusedIndex === index && (               
                      <Button onClick={() => {                      
                        const selectedNote = formik?.values?.Patient?.CriticNotes[index];   
                        const noteNumber = selectedNote?.NoteNumber;
                        handleSaveNote(selectedNote, noteNumber);                                                                                                                                           
                      }}>
                        <IconCheck />
                        <Typography sx={{ pl: 0.5}} >Done</Typography>
                      </Button> 
                    )}             
                  </Grid>
                </>
              ))}
            </Grid>
          </>
        )}
      />
    </>
  );
};

export default CriticNotes;
